import React, { ReactElement, ReactNode } from "react";
import { NextPage } from "next";
import type { AppProps, NextWebVitalsMetric } from "next/app";
import { useRouter } from "next/router";
import { IntlProvider } from "react-intl";
import { GtmScript } from "@foundation/nextjs-gtm";
import { reportWebVitals as _reportWebVitals } from "@foundation/nextjs-web-vitals";
import { NotifyUnsupportedBrowser } from "@foundation/notify-unsupported-browser";
import { ScreenProvider } from "@foundation/screen";

import { Locale } from "~constants/i18n";
import { onLocaleError } from "~i18n/errorHandler";
import { getProcessEnv } from "~utils/getProcessEnv";

import "~styles/global.css";
import "@bloomberg/gifted-article/index.esm.css";
import "~utils/abortsignalPolyfill";
import "@bloomberg/intercom/src/components/styles/variables.css";

const regUIClientCSS = require("@foundation/reg-ui-client-css");

const s3Domain = getProcessEnv().NEXT_PUBLIC_ASSET_HOST;

export type getLayoutFn = (page: ReactElement) => ReactNode;

export type NextPageWithLayout<P, IP = P> = NextPage<P, IP> & {
    getLayout?: getLayoutFn;
};

type AppPropsWithLayout<P> = AppProps & {
    Component: NextPageWithLayout<P>;
    pageProps: Record<string, any>;
};

const default_layout = (page: ReactElement) => page;

/**
 * DO NOT REMOVE: This function is called by NextJS framework
 */
export function reportWebVitals(metric: NextWebVitalsMetric) {
    const isPreviewPage = window.location.href.includes("/preview/");

    return isPreviewPage ? undefined : _reportWebVitals(metric);
}

// We are using a custom root App here to inject the global css
// used by Tailwind to all pages
export default function NextApp({
    Component,
    pageProps,
}: AppPropsWithLayout<Record<string, any>>) {
    const getLayout: getLayoutFn = Component.getLayout ?? default_layout;
    const { locale, defaultLocale } = useRouter();
    return (
        <IntlProvider
            locale={locale ?? defaultLocale ?? Locale.En}
            defaultLocale={Locale.En}
            messages={{}}
            onError={onLocaleError}
        >
            <ScreenProvider>
                <style jsx global>
                    {regUIClientCSS}
                </style>
                <GtmScript analytics={pageProps.analytics} />
                {getLayout(<Component {...pageProps} />)}
                {s3Domain && <NotifyUnsupportedBrowser s3Domain={s3Domain} />}
            </ScreenProvider>
        </IntlProvider>
    );
}
